import React, { useState, useMemo } from 'react'

import { Plus, Edit2, Trash2, Search, Phone, Mail, Calendar } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function ActivitiesPage() {
  const activities = useCRMStore((state) => state.activities)
  const customers = useCRMStore((state) => state.customers)
  const addActivity = useCRMStore((state) => state.addActivity)
  const updateActivity = useCRMStore((state) => state.updateActivity)
  const deleteActivity = useCRMStore((state) => state.deleteActivity)

  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [formData, setFormData] = useState({
    type: 'Call',
    customerId: 1,
    description: '',
    date: new Date().toISOString().split('T')[0],
    user: 'Admin',
    status: 'Completed',
  })

  const filteredActivities = useMemo(() => {
    return activities.filter(activity =>
      activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customers.find(c => c.id === activity.customerId)?.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [activities, customers, searchTerm])

  const sortedActivities = useMemo(() => {
    return [...filteredActivities].sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [filteredActivities])

  const handleOpenModal = (activity = null) => {
    if (activity) {
      setSelectedActivity(activity)
      setFormData(activity)
    } else {
      setSelectedActivity(null)
      setFormData({
        type: 'Call',
        customerId: 1,
        description: '',
        date: new Date().toISOString().split('T')[0],
        user: 'Admin',
        status: 'Completed',
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedActivity(null)
    setFormData({
      type: 'Call',
      customerId: 1,
      description: '',
      date: new Date().toISOString().split('T')[0],
      user: 'Admin',
      status: 'Completed',
    })
  }

  const handleSave = () => {
    if (!formData.description || !formData.customerId) {
      alert('Please fill in all required fields')
      return
    }

    if (selectedActivity) {
      updateActivity(selectedActivity.id, formData)
    } else {
      addActivity(formData)
    }
    handleCloseModal()
  }

  const handleDelete = (activityId) => {
    if (confirm('Are you sure you want to delete this activity?')) {
      deleteActivity(activityId)
    }
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'Call': return <Phone size={18} />
      case 'Email': return <Mail size={18} />
      case 'Meeting': return <Calendar size={18} />
      default: return null
    }
  }

  const getActivityColor = (type) => {
    switch (type) {
      case 'Call': return 'bg-blue-500/20 text-blue-300'
      case 'Email': return 'bg-purple-500/20 text-purple-300'
      case 'Meeting': return 'bg-green-500/20 text-green-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-500/20 text-green-300'
      case 'Scheduled': return 'bg-yellow-500/20 text-yellow-300'
      case 'Pending': return 'bg-orange-500/20 text-orange-300'
      case 'Cancelled': return 'bg-red-500/20 text-red-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getCustomerName = (customerId) => {
    return customers.find(c => c.id === customerId)?.name || 'Unknown'
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Activities</h1>
              <p className="text-slate-400">Log and track customer interactions</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              <Plus size={20} />
              Log Activity
            </button>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Total Activities</p>
              <p className="text-3xl font-bold text-white">{activities.length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Calls</p>
              <p className="text-3xl font-bold text-blue-400">{activities.filter(a => a.type === 'Call').length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Emails</p>
              <p className="text-3xl font-bold text-purple-400">{activities.filter(a => a.type === 'Email').length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Meetings</p>
              <p className="text-3xl font-bold text-green-400">{activities.filter(a => a.type === 'Meeting').length}</p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-400/50"
              />
            </div>
          </div>

          {/* Activities List */}
          <div className="space-y-4">
            {sortedActivities.length > 0 ? (
              sortedActivities.map((activity) => (
                <div key={activity.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-4 flex-1">
                      <div className={`p-3 rounded-lg ${getActivityColor(activity.type)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-2 items-center mb-2">
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getActivityColor(activity.type)}`}>
                            {activity.type}
                          </span>
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(activity.status)}`}>
                            {activity.status}
                          </span>
                        </div>
                        <p className="text-white font-semibold mb-1">{getCustomerName(activity.customerId)}</p>
                        <p className="text-slate-400 text-sm mb-2">{activity.description}</p>
                        <div className="flex gap-4 text-xs text-slate-500">
                          <span>Date: {new Date(activity.date).toLocaleDateString()}</span>
                          <span>By: {activity.user}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenModal(activity)}
                        className="p-2 hover:bg-slate-700 rounded transition-colors text-blue-400"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(activity.id)}
                        className="p-2 hover:bg-slate-700 rounded transition-colors text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-400 text-lg">No activities found</p>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Activity Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b border-blue-500/20">
                <h2 className="text-2xl font-bold text-white">
                  {selectedActivity ? 'Edit Activity' : 'Log New Activity'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    <option>Call</option>
                    <option>Email</option>
                    <option>Meeting</option>
                  </select>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: parseInt(e.target.value) })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  />
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    <option>Completed</option>
                    <option>Scheduled</option>
                    <option>Pending</option>
                    <option>Cancelled</option>
                  </select>
                  <input
                    type="text"
                    placeholder="User"
                    value={formData.user}
                    onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <textarea
                    placeholder="Description *"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400 min-h-24"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end gap-4 p-6 border-t border-blue-500/20">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 rounded border border-slate-600 text-slate-300 hover:border-slate-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded transition-all"
                >
                  Save Activity
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )
}
